import { useState, useCallback } from 'react';
import firebase from 'firebase/app';
import { useInjection } from '../../../di_container/InjectionProvider';
import { Utils } from '../../../common/helper';
import { useClinicUser } from '../clinic/ClinicUserHooks';
import { useTranslation } from 'react-i18next';
import { Chat } from '../../firestore_model';
import { pagination } from '../../../common/constants/Constants';
import { Timestamp, firestore } from '@1amageek/ballcap';
import { LatestMessage } from '../../firestore_model/chat/entity/LatestMessage';
import { Room } from '../../firestore_model/chat';
export const useChat = () => {
    const columns = [
        { id: 'userName', label: 'ワーカー', minWidth: 80 },
        { id: 'latestMessage', label: '最新のメッセージ', minWidth: 150 },
    ];
    const { container } = useInjection();
    const { jobClinic } = useClinicUser();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    // チャットルームの一覧を取得
    const loadChatList = useCallback((listenerCallback, lastDocument) => {
        var _a;
        setLoading(true);
        try {
            if (Utils.isNull(jobClinic)) {
                throw new Error(t('システムエラー'));
            }
            const chatClinic = new Chat.Clinic((_a = jobClinic) === null || _a === void 0 ? void 0 : _a.id);
            const firestoreRepository = container.resolve('FirestoreRepository');
            const query = firestoreRepository.query({
                path: chatClinic.myRooms.collectionReference.path,
                isGroup: false,
                limit: pagination.limit,
                startAfter: lastDocument,
                orderBy: {
                    fieldPath: 'latestMessage.createdAt',
                    directionStr: 'desc',
                },
            });
            const listener = query.onSnapshot((snapshot) => {
                if (snapshot.docs.length > 0) {
                    const myRooms = snapshot.docs.map((myRoom) => Chat.MyRoom.fromSnapshot(myRoom));
                    listenerCallback(myRooms);
                }
            });
            setLoading(false);
            return listener;
        }
        catch (e) {
            setLoading(false);
            throw e;
        }
    }, [jobClinic]);
    // メッセージ一覧の取得
    const loadMessages = useCallback(async (id, lastDocument) => {
        setLoading(true);
        try {
            if (Utils.isNull(jobClinic)) {
                throw new Error(t('システムエラー'));
            }
            const chatRoom = new Chat.Room(id);
            const firestoreRepository = container.resolve('FirestoreRepository');
            const result = await firestoreRepository.loadDocuments({
                path: chatRoom.messages.collectionReference.path,
                isGroup: false,
                limit: pagination.limit,
                startAfter: lastDocument,
                orderBy: {
                    fieldPath: 'createdAt',
                    directionStr: 'desc',
                },
            });
            setLoading(false);
            return {
                data: result.docs.map((item) => Chat.Message.fromSnapshot(item)),
                lastDocument: result.lastDocument,
            };
        }
        catch (e) {
            setLoading(false);
            throw e;
        }
    }, [jobClinic]);
    // 先頭のメッセージを監視
    const listenFirstMessage = useCallback((id, callback) => {
        console.log('listen first message');
        const chatRoom = new Chat.Room(id);
        const firestoreRepository = container.resolve('FirestoreRepository');
        const result = firestoreRepository.query({
            path: chatRoom.messages.collectionReference.path,
            isGroup: false,
            limit: 1,
            orderBy: {
                fieldPath: 'createdAt',
                directionStr: 'desc',
            },
        });
        const listener = result.onSnapshot((snapshot) => {
            if (snapshot.docs.length > 0) {
                const item = Chat.Message.fromSnapshot(snapshot.docs[0]);
                callback(item);
            }
        });
        return listener;
    }, []);
    // myRoomIdからルームの取得
    const loadMyRoom = useCallback(async (id) => {
        var _a;
        setLoading(true);
        try {
            if (Utils.isNull(jobClinic)) {
                throw new Error(t('システムエラー'));
            }
            const chatClinic = new Chat.Clinic((_a = jobClinic) === null || _a === void 0 ? void 0 : _a.id);
            const myRoomSnap = await chatClinic.myRooms.collectionReference.doc(id).get();
            const myRoom = Chat.MyRoom.fromSnapshot(myRoomSnap);
            setLoading(false);
            return myRoom;
        }
        catch (e) {
            setLoading(false);
            throw e;
        }
    }, [jobClinic]);
    // メッセージの送信
    const sendMessage = useCallback(async (myRoom, messageText, messageImage) => {
        var _a, _b, _c;
        setLoading(true);
        try {
            if (Utils.isNull(myRoom)) {
                throw new Error(t('システムエラー'));
            }
            const chatUser = new Chat.User((_a = myRoom.members) === null || _a === void 0 ? void 0 : _a.workerId);
            const workerMyRoom = chatUser.myRooms.doc(myRoom.roomId, Chat.MyRoom);
            const room = new Chat.Room(myRoom.roomId);
            if (Utils.isNull(workerMyRoom) || Utils.isNull(room)) {
                throw new Error(t('システムエラー'));
            }
            const message = new Chat.Message();
            message.isUnread = true;
            message.roomId = myRoom.roomId;
            message.userId = (_b = myRoom.members) === null || _b === void 0 ? void 0 : _b.clinicId;
            message.text = messageText;
            message.images = [];
            const latestMessage = new LatestMessage();
            latestMessage.messageId = message.id;
            latestMessage.text =
                messageImage !== undefined && messageText === '' ? t('画像が送信されました。') : messageText;
            latestMessage.userId = (_c = myRoom.members) === null || _c === void 0 ? void 0 : _c.clinicId;
            latestMessage.createdAt = Timestamp.now();
            if (messageImage !== undefined) {
                const path = message.imageStoragePath(myRoom);
                const storageRepository = container.resolve('CloudStorageRepository');
                const result = await storageRepository.upload(messageImage, Utils.getRandomString(), path);
                message.setImageFromCloudStorage(result);
            }
            else {
                message.images = [];
            }
            await firestore.runTransaction(async (t) => {
                var _a;
                const now = Timestamp.now();
                t.set(room.messages.collectionReference.doc(message.id), {
                    ...message.data(),
                    createdAt: now,
                    updatedAt: now,
                });
                t.update(workerMyRoom.documentReference, {
                    ...workerMyRoom.data(),
                    latestMessage: latestMessage.data(),
                    roomId: myRoom.roomId,
                    members: (_a = myRoom.members) === null || _a === void 0 ? void 0 : _a.data(),
                    unreadCount: firebase.firestore.FieldValue.increment(1),
                    updatedAt: now,
                });
                t.update(myRoom.documentReference, {
                    ...myRoom.data(),
                    latestMessage: latestMessage.data(),
                    updatedAt: now,
                });
            });
            setLoading(false);
        }
        catch (e) {
            setLoading(false);
            console.log(e);
            throw e;
        }
    }, []);
    // 既読情報のリセット
    const resetUnreadData = useCallback(async (myRoom) => {
        var _a;
        try {
            if (Utils.isNull(myRoom)) {
                throw new Error(t('システムエラー'));
            }
            console.log('resetUnreadData');
            const batch = firestore.batch();
            myRoom.unreadCount = 0;
            batch.update(myRoom.documentReference, myRoom.data());
            const room = new Room(myRoom.roomId);
            const firestoreRepository = container.resolve('FirestoreRepository');
            const result = await firestoreRepository.loadDocuments({
                path: room.messages.collectionReference.path,
                isGroup: false,
                limit: 1000,
                whereList: [
                    { fieldPath: 'userId', opStr: '==', value: (_a = myRoom.members) === null || _a === void 0 ? void 0 : _a.workerId },
                    { fieldPath: 'isUnread', opStr: '==', value: true },
                ],
                orderBy: {
                    fieldPath: 'createdAt',
                    directionStr: 'desc',
                },
            });
            result.docs
                .map((item) => Chat.Message.fromSnapshot(item))
                .forEach((message) => {
                const data = message.toResetUnreadData(false);
                batch.update(myRoom.documentReference, 'roomId', myRoom.roomId, ...data);
            });
            await batch.commit();
        }
        catch (e) {
            console.log(e);
            throw e;
        }
    }, []);
    // clinicIdとwordkerIdからルームの取得
    const loadMyRoomIdByWorkerId = useCallback(async (clinicId, workerId) => {
        try {
            const chatClinic = new Chat.Clinic(clinicId);
            const firestoreRepository = container.resolve('FirestoreRepository');
            const result = await firestoreRepository.loadDocuments({
                path: chatClinic.myRooms.collectionReference.path,
                whereList: [{ fieldPath: 'members.workerId', opStr: '==', value: workerId }],
                isGroup: false,
                limit: 1,
            });
            const myRoom = result.docs.map((item) => Chat.MyRoom.fromSnapshot(item));
            return myRoom.length === 0 || Utils.isNull(myRoom[0]) ? null : myRoom[0].id;
        }
        catch (e) {
            throw e;
        }
    }, []);
    return {
        columns,
        loading,
        loadChatList,
        loadMessages,
        loadMyRoom,
        listenFirstMessage,
        sendMessage,
        resetUnreadData,
        loadMyRoomIdByWorkerId,
    };
};
